import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Coffee, Facebook, Instagram, Phone, Mail, MapPin } from 'lucide-react';

// Asset URLs
const ASSETS = {
  hero: "https://customer-assets.emergentagent.com/job_4c827c7f-e4f3-4bfd-8bb5-2ad866a4f807/artifacts/aalb4tmi_INDOOR%20PHOTO%205.jpg",
  heritage: "https://customer-assets.emergentagent.com/job_4c827c7f-e4f3-4bfd-8bb5-2ad866a4f807/artifacts/6x7ht9ul_INDOOR%20PHOTO%202%20.jpg",
  brick: "https://customer-assets.emergentagent.com/job_4c827c7f-e4f3-4bfd-8bb5-2ad866a4f807/artifacts/gkzd9i4p_INDOOR%20PHOTO%201.jpg",
  outdoor: "https://customer-assets.emergentagent.com/job_4c827c7f-e4f3-4bfd-8bb5-2ad866a4f807/artifacts/kp0pmrba_OUTDOOR%20PHOTO%201.jpg",
  details: "https://customer-assets.emergentagent.com/job_4c827c7f-e4f3-4bfd-8bb5-2ad866a4f807/artifacts/m4byw70a_INDOOR%20PHOTO%203.jpg",
};

// Contact Info
const CONTACT = {
  phone: "01908 787085",
  email: "Thebeaninstony@gmail.com",
  buffetEmail: "Thebeaninstonybuffet@gmail.com",
  address: "75 High St, Stony Stratford, Milton Keynes MK11 1AY",
  facebook: "https://www.facebook.com/beaninstony",
  instagram: "https://www.instagram.com/bean_in_stony/",
  whatsappQR: "https://customer-assets.emergentagent.com/job_heritage-bean-co/artifacts/nsijt83r_Screenshot%202026-03-10%20at%2021.26.58.png"
};

// Navigation Component
export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Menu', path: '/menu' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-cream shadow-heritage py-3' : 'bg-transparent py-5'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <Coffee className={`w-8 h-8 transition-colors ${scrolled ? 'text-walnut' : 'text-white drop-shadow-md'}`} />
            <span className={`font-serif text-2xl font-semibold transition-colors ${scrolled ? 'text-walnut' : 'text-white drop-shadow-md'}`}>
              Bean in Stony
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-sans font-semibold uppercase tracking-widest text-sm transition-colors ${
                  scrolled 
                    ? `text-walnut hover:text-sage ${location.pathname === link.path ? 'text-sage' : ''}` 
                    : `text-white hover:text-white/80 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] ${location.pathname === link.path ? 'text-sage-light' : ''}`
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden p-2 ${scrolled ? 'text-walnut' : 'text-cream'}`}
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-walnut/20" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block py-3 nav-link ${
                  location.pathname === link.path ? 'text-sage' : ''
                }`}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
export const Footer = () => {
  return (
    <footer className="bg-walnut text-cream" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 py-16 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Coffee className="w-8 h-8 text-sage" />
              <span className="font-serif text-2xl font-semibold">Bean in Stony</span>
            </div>
            <p className="font-accent text-xl text-sage-light mb-4">
              "Stony Stratford's Hidden Gem"
            </p>
            <p className="text-cream/80 text-sm leading-relaxed">
              Nestled in a historic 1700s apothecary building, we serve artisan coffee 
              and homemade treats in the heart of Stony Stratford.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-serif text-xl mb-6">Visit Us</h3>
            <div className="space-y-4">
              <a 
                href={`tel:${CONTACT.phone}`} 
                className="flex items-center gap-3 text-cream/80 hover:text-sage transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5" />
                <span>{CONTACT.phone}</span>
              </a>
              <a 
                href={`mailto:${CONTACT.email}`} 
                className="flex items-center gap-3 text-cream/80 hover:text-sage transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-5 h-5" />
                <span>{CONTACT.email}</span>
              </a>
              <div className="flex items-start gap-3 text-cream/80">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{CONTACT.address}</span>
              </div>
            </div>
          </div>

          {/* Social */}
          <div>
            <h3 className="font-serif text-xl mb-6">Follow Us</h3>
            <div className="flex gap-4">
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-cream/10 hover:bg-sage p-3 rounded-full transition-all duration-300 hover:-translate-y-1"
                data-testid="footer-facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-cream/10 hover:bg-sage p-3 rounded-full transition-all duration-300 hover:-translate-y-1"
                data-testid="footer-instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>
            <p className="mt-6 text-cream/60 text-sm">
              Share your experience with #BeanInStony
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-cream/20 text-center text-cream/60 text-sm">
          <p>&copy; {new Date().getFullYear()} Bean in Stony. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export { ASSETS, CONTACT };
